import React, { useEffect, useMemo, useState } from "react";
import { Loader2, CheckCircle2, Lock, Search, ListChecks, Save } from "lucide-react";
import { toast } from "sonner";
import customerApi from "./customerApi";
import { SORT_OPTIONS, distinctColours, applyCatalogControls } from "../hooks/catalogSort";

/* HQ , Choose My Tubers.
   Separate from VIP Build-a-Box: when Heritage creates an order with a
   catalogue-pick allowance, the customer chooses that many varieties here
   once the order is PAID, and the choices save straight onto the order. */
const AUBERGINE = "var(--hq-deep, #5A4565)";
const PLUM = "var(--hq-accent, #6F4A7A)";
const PARCH = "#D8C9A3";
const GOLD = "#B08D3E";

export default function HQMyPicks() {
  const [orders, setOrders] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [o, c] = await Promise.all([
        customerApi.get("/customer/pick-orders"),
        customerApi.get("/catalog"),
      ]);
      setOrders(Array.isArray(o.data) ? o.data : []);
      setCatalog(Array.isArray(c.data) ? c.data : []);
    } catch {
      toast.error("Couldn't load your pick orders.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="hq-my-picks" className="space-y-5" style={{ color: AUBERGINE }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Heritage Headquarters</div>
        <h1 className="font-serif text-3xl mt-1">Choose My Tubers</h1>
        <p className="text-sm mt-1 opacity-80">
          When an order comes with a tuber allowance, pick your varieties from our catalogue here.
          Picks unlock once your payment is received.
        </p>
      </header>

      {loading ? (
        <div className="inline-flex items-center gap-2 text-sm opacity-70"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : orders.length === 0 ? (
        <div className="bg-white border rounded-md p-10 text-center text-sm opacity-70" style={{ borderColor: PARCH }} data-testid="hq-picks-empty">
          <ListChecks className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No orders with catalogue picks yet. When Heritage sets one up for you, it appears here.
        </div>
      ) : (
        orders.map((o) => <PickOrderCard key={o.id} order={o} catalog={catalog} onSaved={load} />)
      )}
    </div>
  );
}

function PickOrderCard({ order, catalog, onSaved }) {
  const limit = Number(order.hq_pick_count || 0);
  const alreadyPicked = (order.hq_picked_varieties || []).length > 0 || (order.hq_picked_variety_ids || []).length > 0;
  const paid = order.payment_status === "paid";
  const [selected, setSelected] = useState(() => new Set(order.hq_picked_variety_ids || []));
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("az");
  const [colour, setColour] = useState("all");
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(alreadyPicked);

  const colours = useMemo(() => distinctColours(catalog), [catalog]);
  const filtered = useMemo(
    () => applyCatalogControls(catalog, { q, sort, colour }),
    [catalog, q, sort, colour]
  );

  const toggle = (id) => {
    if (locked) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < limit) next.add(id);
      else toast.error(`You can pick ${limit} tubers on this order.`);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await customerApi.post(`/customer/orders/${order.id}/console-picks`, { variety_ids: [...selected] });
      toast.success("Your picks are saved, Heritage will pack your order.");
      setLocked(true);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your picks.");
    } finally { setSaving(false); }
  };

  const pickedNames = (order.hq_picked_varieties || []).map((v) => v.name).filter(Boolean);

  return (
    <section className="bg-white border rounded-md p-4 md:p-5" style={{ borderColor: PARCH }} data-testid={`pick-order-${order.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: GOLD }}>
            Order #{(order.id || "").slice(0, 8)} · {order.created_at ? new Date(order.created_at).toLocaleDateString("en-NZ") : ""}
          </div>
          <div className="font-serif text-lg mt-0.5">
            {limit} tuber{limit === 1 ? "" : "s"} of your choice
          </div>
        </div>
        <div className="text-right text-sm">
          NZ${Number(order.total || 0).toFixed(2)}
          <div className={`text-[10px] tracking-[0.25em] uppercase font-semibold mt-0.5 ${paid ? "text-green-700" : "text-[#A33030]"}`}>
            {paid ? "Paid" : "Awaiting payment"}
          </div>
        </div>
      </div>

      {!paid && !alreadyPicked ? (
        <div className="mt-4 flex items-center gap-2 text-sm bg-[#F6F0E4] border rounded p-3" style={{ borderColor: PARCH }} data-testid={`picks-locked-${order.id}`}>
          <Lock className="w-4 h-4 shrink-0" style={{ color: PLUM }} />
          Your picks unlock as soon as your payment is received. Bank details are on your invoice.
        </div>
      ) : locked ? (
        <div className="mt-4" data-testid={`picks-done-${order.id}`}>
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-green-800">
            <CheckCircle2 className="w-4 h-4" /> Picks submitted{order.hq_picks_submitted_at ? ` · ${new Date(order.hq_picks_submitted_at).toLocaleDateString("en-NZ")}` : ""}
          </div>
          {pickedNames.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {pickedNames.map((n) => (
                <li key={n} className="px-2.5 py-1 text-xs rounded-full text-white" style={{ backgroundColor: PLUM }}>{n}</li>
              ))}
            </ul>
          )}
          <p className="text-xs opacity-70 mt-2">Need a change? Send us a note via Contact Heritage.</p>
        </div>
      ) : (
        <div className="mt-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F6F0E4] border rounded flex-1 max-w-xs" style={{ borderColor: PARCH }}>
              <Search className="w-3.5 h-3.5 opacity-50" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search the catalogue…"
                data-testid={`picks-search-${order.id}`}
                className="flex-1 bg-transparent outline-none text-sm"
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid={`picks-sort-${order.id}`}
              className="px-2 py-1.5 text-xs bg-white border rounded"
              style={{ borderColor: PARCH }}
            >
              {SORT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <select
              value={colour}
              onChange={(e) => setColour(e.target.value)}
              data-testid={`picks-colour-${order.id}`}
              className="px-2 py-1.5 text-xs bg-white border rounded max-w-full"
              style={{ borderColor: PARCH }}
            >
              <option value="all">All colours</option>
              {colours.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="text-sm font-semibold" style={{ color: PLUM }} data-testid={`picks-count-${order.id}`}>
              {selected.size} of {limit} chosen
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[420px] overflow-y-auto pr-1">
            {filtered.map((v) => {
              const on = selected.has(v.id);
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => toggle(v.id)}
                  data-testid={`pick-variety-${v.id}`}
                  className={`text-left border rounded overflow-hidden transition ${on ? "ring-2" : "hover:shadow"}`}
                  style={{ borderColor: on ? PLUM : PARCH, ...(on ? { boxShadow: `0 0 0 2px ${PLUM}` } : {}) }}
                >
                  {v.image ? (
                    <img src={v.image} alt={v.name} loading="lazy" className="w-full h-20 object-cover" />
                  ) : (
                    <div className="w-full h-20 grid place-items-center bg-[#F6F0E4] text-2xl">✿</div>
                  )}
                  <div className="px-2 py-1.5 flex items-center justify-between gap-1">
                    <span className="text-xs truncate">{v.name}</span>
                    {on && <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: PLUM }} />}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-end gap-3">
            {selected.size !== limit && (
              <span className="text-xs opacity-70">Choose {limit - selected.size} more to finish.</span>
            )}
            <button
              type="button"
              onClick={save}
              disabled={saving || selected.size !== limit}
              data-testid={`picks-save-${order.id}`}
              className="px-5 py-2.5 rounded text-[11px] tracking-[0.25em] uppercase font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-40"
              style={{ backgroundColor: PLUM }}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save my picks
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
